import {
  PdfGen,
  PdfUnit,
  PdfSize,
  PdfRect,
  PdfPoint,
  PdfBrush,
  PdfFont,
  ETextAlign,
} from '../../pdf/pdf';
import { ILayoutLabelGen } from '../layoutLabel';
import { IItemCount } from '@educraft/base';
import { OptsLabel } from '../optsLabel';
import { GUtil } from '../../pdf/gutil';
import '../../pdf/draw/primitives';
import '../../pdf/draw/string';
import '../../pdf/draw/label';

/**
 * Vojtik - Switch puzzle with scrambled label mapping
 *
 * Layout: 1 portrait page.
 * Left side: 2×5 grid of A labels (image/text).
 * Right side: 5×2 grid of B labels (text), reordered by a custom PRNG
 * based on 3 random switches. Small switch indicator dots at top-left.
 *
 * Migrated from C# EduFrame.Img10.Generators.Vojtik
 */
export class Vojtik implements ILayoutLabelGen, IItemCount {
  countDefault(): number {
    return 10;
  }
  countMin(): number {
    return 10;
  }
  countMax(): number {
    return 10;
  }

  /**
   * Generate a shuffle table using a custom PRNG seeded by 3 binary switches.
   */
  private makeTable(swx: boolean[]): number[] {
    const sw = (swx[0] ? 1 : 0) | (swx[1] ? 2 : 0) | (swx[2] ? 4 : 0);
    let seed = 12345 + sw;

    const random = (): number => {
      seed = (seed * 48271 + 7) & 0xffff;
      return seed;
    };

    const rndTable = Array.from({ length: 10 }, (_, i) => i);

    for (let i = 0; i < 50; i++) {
      const i1 = random() % 10;
      const i2 = random() % 10;
      const tmp = rndTable[i1];
      rndTable[i1] = rndTable[i2];
      rndTable[i2] = tmp;
    }

    return rndTable;
  }

  public async generate(pdf: PdfGen, opts: OptsLabel): Promise<void> {
    const sw1 = opts.data.random.next(0, 2) !== 0;
    const sw2 = opts.data.random.next(0, 2) !== 0;
    const sw3 = opts.data.random.next(0, 2) !== 0;
    const swx = [sw1, sw2, sw3];

    const table = this.makeTable(swx);
    // Inverse table: invTable[table[i]] = i
    const invTable: number[] = new Array(table.length);
    for (let i = 0; i < table.length; i++) {
      invTable[table[i]] = i;
    }

    const margin = PdfUnit.FromMillimeters(10);
    const a5h = PdfUnit.FromMillimeters(148);
    const space = PdfUnit.FromMillimeters(2);
    const width = PdfUnit.FromMillimeters(215);

    const d_w = (width - 2 * margin - 6 * space) / 7;
    const d_h1 = (a5h - 2 * margin - 4 * space) / 5;
    const d_h2 = (a5h - 2 * margin - 1 * space) / 2;

    const sz1 = { width: d_h1, height: d_w };
    const sz2 = { width: d_h2, height: d_w };

    const sheetSize = { width: PdfSize.A4.height / 2, height: PdfSize.A4.width };
    const sheetPoint = new PdfPoint(
      (PdfSize.A4.width - sheetSize.width) / 2,
      (PdfSize.A4.height - sheetSize.height) / 2
    );
    const sheetRect = new PdfRect(sheetPoint.x, sheetPoint.y, sheetSize.width, sheetSize.height);

    const page = pdf.addPage();

    // Cut lines
    page.drawLine(
      GUtil.cutPen,
      new PdfPoint(0, sheetRect.y),
      new PdfPoint(PdfSize.A4.width, sheetRect.y)
    );
    page.drawLine(
      GUtil.cutPen,
      new PdfPoint(0, sheetRect.y + sheetRect.height),
      new PdfPoint(PdfSize.A4.width, sheetRect.y + sheetRect.height)
    );
    page.drawLine(
      GUtil.cutPen,
      new PdfPoint(sheetRect.x, 0),
      new PdfPoint(sheetRect.x, PdfSize.A4.height)
    );
    page.drawLine(
      GUtil.cutPen,
      new PdfPoint(sheetRect.x + sheetRect.width, 0),
      new PdfPoint(sheetRect.x + sheetRect.width, PdfSize.A4.height)
    );

    page.drawRectangle(GUtil.basePen, null, sheetRect);

    // Switch indicator dots
    const swSize = { width: PdfUnit.FromMillimeters(4), height: PdfUnit.FromMillimeters(4) };
    const swPos = new PdfPoint(sheetRect.x + PdfUnit.FromMillimeters(1), sheetRect.y + margin);
    const swD = PdfUnit.FromMillimeters(10);

    for (let i = 0; i < swx.length; i++) {
      for (let j = 0; j < 2; j++) {
        const rect = new PdfRect(
          swPos.x + swSize.width * j,
          swPos.y + swD * i,
          swSize.width,
          swSize.height
        );

        if (swx[i] === (j === 0)) {
          page.drawRectangle(GUtil.basePen, null, rect);
        } else {
          page.drawRectangle(null, PdfBrush.Black, rect);
        }
      }
    }

    const dd = PdfUnit.FromMillimeters(6);

    const leftTexts = opts.list.map((item) => [item.left.getText() ?? '']);
    const rightTexts = opts.list.map((item) => [item.right.getText() ?? '']);

    const leftFontSize =
      leftTexts.length > 0
        ? page.adjustFontMulti(leftTexts, GUtil.baseFont, new PdfSize(sz2.width - dd, sz2.height))
        : GUtil.baseFont;

    const rightFontSize =
      rightTexts.length > 0
        ? page.adjustFontMulti(rightTexts, GUtil.baseFont, new PdfSize(sz1.width, sz1.height * 0.6))
        : GUtil.baseFont;

    for (let x = 0; x < 2; x++) {
      for (let y = 0; y < 5; y++) {
        const index1 = x * 5 + y;
        const index2 = x * 5 + y;
        if (index1 >= opts.list.length) continue;

        // A label (left side - larger cells)
        const p1 = new PdfPoint(
          sheetRect.x + margin + x * (d_h2 + space),
          sheetRect.y + margin + y * (d_w + space)
        );
        const r1 = new PdfRect(p1.x, p1.y, sz2.width, sz2.height);

        const r1x = new PdfRect(r1.x + (x === 0 ? 1 : 0) * dd, r1.y, r1.width - dd, r1.height);
        const r1d = new PdfRect(
          r1.x + (x === 0 ? dd / 2 : r1.width - dd - dd / 2),
          r1.y + (r1.height - dd) / 2,
          dd,
          dd
        );

        page.drawLabel(opts.list[index1].left, { rect: r1x, font: leftFontSize });

        page.drawEllipse(null, PdfBrush.White, r1d);
        page.drawEllipse(GUtil.basePen, null, r1d);

        // B label (right side - smaller cells)
        const p2 = new PdfPoint(
          sheetRect.x + margin + y * (d_h1 + space),
          sheetRect.y + margin + (5 + x) * (d_w + space)
        );
        const r2 = new PdfRect(p2.x, p2.y, sz1.width, sz1.height);
        const r2d = new PdfRect(r2.x + (r2.width - dd) / 2, r2.y + r2.height - dd - dd / 2, dd, dd);

        page.drawEllipse(GUtil.basePen, null, r2d);

        if (invTable[index2] < opts.list.length) {
          page.drawLabel(opts.list[invTable[index2]].right, {
            rect: new PdfRect(r2.x, r2.y, r2.width, r2.height * 0.6),
            font: rightFontSize,
          });
        }
      }
    }
  }
}
